import { autoinject, transient } from "aurelia-dependency-injection";
import { CustomerComplianceCheckEmailModel, CustomerDueDiligenceApprovalEmailModel, CustomerDueDiligenceEmailModel } from "../../../models/due-diligence.models";
import { SeniorApprovalApi } from "../../../apis/senior-approval.api";
import { ComplianceCheckApi } from "../../../apis/compliance-check.api";
import { CustomerApi } from "../../../apis/customer.api";
import { CloneHelper } from "@rightship-tech/common-ui/helpers/clone.helper";
import { UserClaimsService } from "@rightship-tech/common-ui/services/basic/user-claims.service";
import { DueDiligenceClaims } from "../../../constants/claims.constants";
import { Claims } from "@rightship-tech/common-ui/constants/authentication/claims";
import { UserClientService } from "@rightship-tech/common-ui/services/basic/user-client.service";
import { NotificationClientService, NotificationType } from "@rightship-tech/common-ui/services/basic/notification-client.service";

@autoinject()
@transient()
export class CustomerDueDiligenceConfiguration {

    private persistedComplianceCheckEmails: CustomerComplianceCheckEmailModel[] = [];
    private persistedSeniorApprovalEmails: CustomerDueDiligenceApprovalEmailModel[] = [];    
    private showNotifications: boolean;
    public seniorApprovalEmails: CustomerDueDiligenceApprovalEmailModel[];
    public complianceCheckEmails: CustomerComplianceCheckEmailModel[];
    public customerEmails: string[];
    public canPassToRightShipVettingQueue: boolean = false;
    public customer: any;
    public showSaveButton: boolean = false;
    
    constructor(private seniorApprovalApi: SeniorApprovalApi, private compliaceCheckApi: ComplianceCheckApi,
        private userClaimsService: UserClaimsService, private customerApi: CustomerApi, private userClientService: UserClientService,
        private notificationClientService: NotificationClientService) { 
        this.canPassToRightShipVettingQueue = userClaimsService.hasPermission(DueDiligenceClaims.ShowDueDiligenceProcessSectionInDueDiligenceConfiguration);
    }

    async activate(model: { customer: any, showSaveButton: boolean, showNotifications: boolean }) {
        this.customer = model.customer;
        this.showSaveButton = model.showSaveButton;
        this.showNotifications = model.showNotifications;
    }

    async attached() {
        try {
            const isRightShipUser = this.userClaimsService.hasPermission(Claims.RightShipUser);
            let customerId: number = this.customer?.customerId;

            if (!customerId && !isRightShipUser) {
                customerId = this.userClientService.currentUser.businessUnit.customerId;
            }

            if (!customerId) {
                return;
            }
            
            Promise.all([
                this.customerApi.getCustomerUserEmails(customerId), //Get customer emails
                this.getComplianceTeamEmails(customerId, isRightShipUser),
                this.getSeniorApprovers(customerId, isRightShipUser)
            ]).then(([customerEmails, complianceCheckEmails, seniorApprovalEmails]) => {
                this.customerEmails = customerEmails;
                this.complianceCheckEmails = complianceCheckEmails;
                this.seniorApprovalEmails = seniorApprovalEmails;

                this.persistedComplianceCheckEmails = CloneHelper.clone(this.complianceCheckEmails);
                this.persistedSeniorApprovalEmails = CloneHelper.clone(this.seniorApprovalEmails);

                if(this.seniorApprovalEmails.length == 0) {
                    this.addSeniorApproverEmail();
                }

                if(this.complianceCheckEmails.length == 0) {
                    this.addComplianceCheckEmail();
                }
            });
        } catch (error) {
            console.error(error);        
        }
    }

    //Add senior approver email
    addSeniorApproverEmail() {
        this.seniorApprovalEmails.push({
            Id: 0,
            email: ""
        });
    }

    //Remove senior approver email
    removeSeniorApproverEmail(index: number) {
        this.seniorApprovalEmails.splice(index, 1);
    }

    //Add compliance check email
    addComplianceCheckEmail() {
        this.complianceCheckEmails.push({
            Id: 0,
            email: ""
        });
    }

    //Remove compliance check email
    removeComplianceCheckEmail(index: number) {
        this.complianceCheckEmails.splice(index, 1);
    }

    public async updateSeniorApprovers(): Promise<boolean> {
        const anySeniorApprovalChanges = !this.areEqual(this.persistedSeniorApprovalEmails, this.seniorApprovalEmails);
        if (anySeniorApprovalChanges) {            
            await this.seniorApprovalApi.updateSeniorApprovals(this.seniorApprovalEmails, this.customer?.customerId);
            return true;
        } else {
            return false;
        }
    }

    public async updateComplianceCheckEmails(): Promise<boolean> {
        const anySeniorApprovalChanges = !this.areEqual(this.persistedComplianceCheckEmails, this.complianceCheckEmails);
        if (anySeniorApprovalChanges) {            
            await this.compliaceCheckApi.updateComplianceCheckEmails(this.complianceCheckEmails, this.customer?.customerId);
            return true;
        } else {
            return false;
        }
    }

    public save() {
        Promise
            .all([this.updateSeniorApprovers(), this.updateComplianceCheckEmails()])
            .then(([updateComplianceCheckResponse, updateSeniorApproversResponse]) => {
                if (this.showNotifications) {
                    if (updateComplianceCheckResponse || updateSeniorApproversResponse) {
                        this.notificationClientService.show("Changes saved successfully", "", NotificationType.Success, 5000);
                    }
                    else {
                        this.notificationClientService.show("No changes performed", "There were no changes to save", NotificationType.Info, 5000);
                    }
                }
            })
            .catch(() => {
                if (this.showNotifications) {
                    this.notificationClientService.show("Failed to save customer!", "", NotificationType.Error, 5000);
                }
            });
    }

    private areEqual(a: CustomerDueDiligenceEmailModel[], b: CustomerDueDiligenceEmailModel[]): boolean {
        const a1 = (a ?? [])?.filter(x => x.email && x.email.trim().length > 0);
        const b1 = (b ?? [])?.filter(x => x.email && x.email.trim().length > 0);

        return a1?.length === b1?.length && a1?.every((v, i) => v.email === b1[i].email && v.Id === b1[i].Id);
    }

    private async getSeniorApprovers(customerId: number, isRightShipUser: boolean): Promise<CustomerDueDiligenceApprovalEmailModel[]> {
        if (isRightShipUser) {
            return (await this.seniorApprovalApi.getCustomerSeniorApprovers(customerId)).seniorApprovers ?? [];
        }
        return (await this.seniorApprovalApi.getSeniorApprovers()).seniorApprovers ?? [];                
    }

    private async getComplianceTeamEmails(customerId: number, isRightShipUser: boolean): Promise<CustomerComplianceCheckEmailModel[]> {
        if (isRightShipUser) {
            return (await this.compliaceCheckApi.getCustomerComplianceTeamEmails(customerId)).complianceChecks ?? [];
        }
        return (await this.compliaceCheckApi.getComplianceTeamEmails()).complianceChecks ?? [];                
    }
}
